import { BlockError } from '@/blocks.04-project/block-error/block-error';
import { LazyInit } from '@/blocks.04-project/lazy-init/lazy-init';
import isNull from 'lodash-es/isNull.js';
import toNumber from 'lodash-es/toNumber';
/***
 Класс поля ввода для промокода
 @event change.iv.promoInput вызывается при изменении промокода пользователем c содержит input.value в event.detail
 @example

 Аттрибуты:
 data-debounce { type: Number, default = 1500 } - время ожидание повторного ввода пользователя

 Классы:
  promo-input_success - промокод доступен
  promo-input_error - промокод не доступен
  promo-input_visible - поле вводе доступно для ввода

 Методы:
  close - закрыть поле ввода
  open - открыть поле ввода
  success - переключает инпут в статус правильно введённого промокода
  error - переключает инпут в статус не правильно введённого промокода
  clearStatus - очищает инпут от всех статусов
*/
class PromoInput {
  /***
   * Конструктор класса
   * @param elem {HTMLElement} DOM элемент с классом promo-input
   */
  constructor(elem) {
    this.BlockError = new BlockError({ blockName: 'PromoInput', destroy: this.destroy });
    if (!elem) return this.BlockError.error('Отсутствует elem');
    this.block = elem;
    this.block.PromoInput = this;
    this.block.AsyncPromoInput = new Promise((resolve) => {
      this.promiseResolve = resolve;
    }).then((ins) => ins);

    this.LazyInit = new LazyInit({ ins: this, callback: this.init, addClass: 'promo-input_inited' });
    this.LazyInit.init();
  }

  init = () => {
    this.link = this.block.querySelector('.promo-input__promo');
    if (!this.link) return this.BlockError.error('Отсутствует .promo-input__promo');

    this.inputWrapper = this.block.querySelector('.from-control-wrapper');
    if (!this.inputWrapper) return this.BlockError.error('Отсутствует .from-control-wrapper');

    this.statusIcon = this.block.querySelector('.promo-input__status');
    if (!this.statusIcon) return this.BlockError.error('Отсутствует .promo-input__status');

    this.input = this.inputWrapper.querySelector('.form-control');
    if (!this.input) return this.BlockError.error('Отсутствует .form-control');

    this.closeBtn = this.inputWrapper.querySelector('[data-control="clear"]');
    if (!this.closeBtn) return this.BlockError.error('Отсутствует [data-control="clear"]');

    this.debounce = toNumber(this.block.dataset['debounce']) || 1500;

    this.link.addEventListener('click', this.openHandler);
    this.closeBtn.addEventListener('click', this.closeHandler);
    this.input.addEventListener('input', this.changeHandler);

    this.promiseResolve(this);
    this.block.AsyncPromoInput = this;
  };

  openHandler = () => {
    this.displayToggle(true);
    this.clearStatus();
  };

  changeHandler = () => {
    clearTimeout(this.timeout);
    this.timeout = setTimeout(this.event, this.debounce);
  };

  closeHandler = () => {
    this.displayToggle(false);
    this.clearStatus();
    this.input.value = '';
    this.event();
  };

  displayToggle = (toggle = null) => {
    if (isNull(toggle)) this.block.classList.toggle('promo-input_visible');
    else this.block.classList.toggle('promo-input_visible', toggle);
  };

  /** Закрывает ввод промокода */
  close = () => {
    this.displayToggle(false);
    this.input.value = '';
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
    this.clearStatus();
  };

  /** Открывает ввод промокода */
  open = () => {
    this.displayToggle(true);
  };

  /*** Успешный ввод промокода */
  success = () => {
    this.clearStatus();
    this.block.classList.add('promo-input_success');
  };

  /*** Не правильный ввод промокода */
  error = () => {
    this.clearStatus();
    this.block.classList.add('promo-input_error');
  };

  /*** Скрыть статусы ввода промокода */
  clearStatus = () => {
    this.block.classList.remove('promo-input_error');
    this.block.classList.remove('promo-input_success');
  };

  /***
   Испускает событие
   @fires change.iv.promoInput - event.detail содержит input.value
   */
  event = () => {
    this.block.dispatchEvent(new CustomEvent('change.iv.promoInput', {
      detail: this.input.value,
    }));
  };

  destroy = () => {
    if (this.LazyInit) this.LazyInit.destroy();
    if (this.link) this.link.removeEventListener('click', this.openHandler);
    if (this.closeBtn) this.closeBtn.removeEventListener('click', this.closeHandler);
    if (this.input) this.input.removeEventListener('input', this.changeHandler);
    if (this.block) {
      this.block.classList.remove('promo-input_inited');
      delete this.block.PromoInput;
      delete this.block.AsyncPromoInput;
    }
  };

  static init() {
    Array.from(document.querySelectorAll('.promo-input:not(.promo-input_inited)')).forEach((elem) => new PromoInput(elem));
  }
}

document.addEventListener('DOMContentLoaded', PromoInput.init);

// Экспорт;
window.Block.PromoInput = PromoInput;
